import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Phone, Calendar, Clock, Scissors, CheckCircle, ShieldCheck } from 'lucide-react';

const GuestBooking = () => {
  const { t } = useTranslation();
  const { isGuest, guestTimeRemaining } = useAuth();
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    barber_id: ''
  });

  // CAPTCHA - Simple math verification
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  const captchaQuestion = useMemo(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-', '×'];
    const operator = operators[Math.floor(Math.random() * 3)];
    
    let answer;
    switch (operator) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        answer = Math.max(num1, num2) - Math.min(num1, num2);
        return { question: `${Math.max(num1, num2)} - ${Math.min(num1, num2)} = ?`, answer };
      case '×':
        answer = num1 * num2;
        break;
      default:
        answer = num1 + num2;
    }
    return { question: `${num1} ${operator} ${num2} = ?`, answer };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyCaptcha = () => {
    if (parseInt(captchaAnswer) === captchaQuestion.answer) {
      setCaptchaVerified(true);
      toast.success('تم التحقق بنجاح! أنت لست روبوت 🎉');
    } else {
      toast.error('الإجابة خاطئة! حاول مرة أخرى');
      setCaptchaAnswer('');
    }
  };

  useEffect(() => {
    fetchBarbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers');
      setBarbers(res.data);
    } catch (error) {
      toast.error('فشل تحميل قائمة الحلاقين');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        datetime: `${formData.date}T${formData.time}:00`
      };
      
      await api.post('/queue/register', payload);
      setSubmitted(true);
      toast.success('تم تسجيل طلبك بنجاح! سيتم خدمتك بأسرع وقت');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تسجيل الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isGuest()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">هذه الصفحة للعملاء فقط</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">تم تسجيل طلبك بنجاح!</h2>
          <p className="text-muted-foreground mb-6">
            سيتم إرسال رسالة واتساب لك قريباً. سياسة الصالون: الموجود أولاً يُخدم أولاً.
          </p>
          <div className="bg-primary/10 rounded-lg p-4 mb-6">
            <p className="text-sm text-primary">
              📱 سيصلك تأكيد على رقم: <span className="font-bold">{formData.phone}</span>
            </p>
          </div>

          {/* Contact Information - Arabic */}
          <div className="bg-secondary/50 rounded-lg p-5 mb-4 text-right border border-white/10">
            <h3 className="text-lg font-bold text-primary mb-3">📞 للتواصل معنا</h3>
            <p className="text-sm text-foreground mb-2">
              للتواصل مع الإدارة عبر الواتساب
            </p>
            <p className="text-sm text-foreground mb-3">
              أو للتواصل المباشر مع الحلاقين بالاتصال المباشر على الرقم:
            </p>
            <a 
              href="https://wa.me/971569810665" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-600 transition-colors"
            >
              <Phone className="w-5 h-5" />
              00971569810665
            </a>
          </div>

          {/* Contact Information - English */}
          <div className="bg-secondary/30 rounded-lg p-5 text-left border border-white/5">
            <h3 className="text-lg font-bold text-primary mb-3">📞 Contact Us</h3>
            <p className="text-sm text-foreground mb-2">
              To contact the administration via WhatsApp
            </p>
            <p className="text-sm text-foreground mb-3">
              Or to contact the barbers directly by calling:
            </p>
            <a 
              href="tel:+971569810665" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors"
            >
              <Phone className="w-5 h-5" />
              00971569810665
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold">تسجيل طلب خدمة</h1>
        <p className="text-muted-foreground mt-2">أهلاً بك في KE Gents Saloon</p>
        {guestTimeRemaining && (
          <p className="text-yellow-500 text-sm mt-2">⏱️ متبقي {guestTimeRemaining} دقيقة للجلسة</p>
        )}
      </div>

      <div className="max-w-lg mx-auto">
        {/* CAPTCHA Verification */}
        {!captchaVerified ? (
          <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-bold">التحقق من الهوية</h2>
              <p className="text-muted-foreground text-sm mt-2">أجب على السؤال التالي للتأكد من أنك لست روبوت</p>
            </div>

            <div className="space-y-4">
              <div className="bg-secondary/50 rounded-lg p-6 text-center">
                <p className="text-2xl font-bold text-primary mb-2">{captchaQuestion.question}</p>
                <p className="text-sm text-muted-foreground">ما هو الناتج؟</p>
              </div>

              <Input
                data-testid="captcha-input"
                type="number"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="أدخل الإجابة"
                className="bg-secondary/50 border-white/10 text-center text-xl"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    verifyCaptcha();
                  }
                }}
              />

              <Button
                data-testid="verify-captcha-btn"
                onClick={verifyCaptcha}
                className="w-full bg-primary text-primary-foreground"
                disabled={!captchaAnswer}
              >
                <ShieldCheck className="w-4 h-4 me-2" />
                تحقق
              </Button>
            </div>

            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-400 text-center">
                🔒 هذا التحقق يحمي الصالون من الطلبات الآلية والمزعجة
              </p>
            </div>
          </div>
        ) : (
          /* Booking Form - Shows after CAPTCHA verification */
          <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-2 mb-6 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-500 text-sm">تم التحقق - أنت لست روبوت ✓</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
            {/* الاسم الثلاثي */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <User className="w-4 h-4 text-primary" />
                الاسم الثلاثي
              </label>
              <Input
                data-testid="guest-name-input"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="أدخل اسمك الثلاثي"
                required
                className="bg-secondary/50 border-white/10"
              />
            </div>

            {/* رقم الموبايل */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Phone className="w-4 h-4 text-primary" />
                رقم الموبايل (واتساب)
              </label>
              <Input
                data-testid="guest-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="05xxxxxxxx"
                required
                className="bg-secondary/50 border-white/10"
                dir="ltr"
              />
            </div>

            {/* اليوم */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Calendar className="w-4 h-4 text-primary" />
                اليوم
              </label>
              <Input
                data-testid="guest-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="bg-secondary/50 border-white/10"
              />
            </div>

            {/* الساعة */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Clock className="w-4 h-4 text-primary" />
                الساعة
              </label>
              <Input
                data-testid="guest-time-input"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
                className="bg-secondary/50 border-white/10"
              />
            </div>

            {/* اختيار الحلاق */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Scissors className="w-4 h-4 text-primary" />
                اختر الحلاق
              </label>
              <Select 
                value={formData.barber_id} 
                onValueChange={(value) => setFormData({ ...formData, barber_id: value })}
                required
              >
                <SelectTrigger data-testid="guest-barber-select" className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder="اختر الحلاق المفضل" />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {barbers.map(barber => (
                    <SelectItem key={barber.id} value={barber.id}>
                      {barber.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              data-testid="submit-booking-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-lg font-bold"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  جاري التسجيل...
                </span>
              ) : (
                'تسجيل الطلب'
              )}
            </Button>
          </form>

            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-400">
                ℹ️ سياسة الصالون: الموجود أولاً يُخدم أولاً. سيتم إرسال رسالة واتساب لتأكيد طلبك.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestBooking;
